// Invite and password-reset links, as they arrive at the web app.
//
// Cerebro's email templates (supabase/templates/*.html) link to the site's
// /set-password page with `token_hash` and `type` in the query. The page spends
// the token (verifyOtp) only when the person presses Continue, so a mail scanner
// that opens the link cannot use it up.
//
// Two other shapes are recognized only so they can be refused visibly:
// - A session in the hash (`#access_token=…&type=recovery`), which Supabase's
//   default templates produce. Anyone can craft one for their own account, so the
//   site never signs in from it.
// - An error redirect (`error`, `error_code`, `error_description`) from a default
//   template link that failed, read from the query or the hash as supabase-js does.

/** Shortest password Cerebro accepts; matches `minimum_password_length` in supabase/config.toml. */
export const MIN_PASSWORD_LENGTH = 8;

export type AuthLinkType = 'invite' | 'recovery';

export type AuthLink =
  /** A Cerebro template link: verified on Continue. */
  | { kind: 'token_hash'; type: AuthLinkType; tokenHash: string }
  /** A session handed over in the hash: never used. */
  | { kind: 'session_in_hash'; type: AuthLinkType }
  /** Supabase rejected the link; `otp_expired` means expired, already used, or unknown. */
  | { kind: 'error'; code: string | null; description: string };

/** The invite, reset, or auth-error link carried by `href`, or null for an ordinary page URL. */
export function parseAuthLink(href: string): AuthLink | null {
  const url = new URL(href);
  const params = new URLSearchParams(url.hash.startsWith('#') ? url.hash.slice(1) : '');
  url.searchParams.forEach((value, key) => params.set(key, value));

  const error = params.get('error');
  const code = params.get('error_code');
  const description = params.get('error_description');
  if (error || code || description) {
    return {
      kind: 'error',
      code: code || null,
      description: description || error || code || 'Supabase rejected the link.',
    };
  }

  const type = params.get('type');
  if (type !== 'invite' && type !== 'recovery') return null;

  const tokenHash = params.get('token_hash');
  if (tokenHash) return { kind: 'token_hash', type, tokenHash };

  if (params.get('access_token')) return { kind: 'session_in_hash', type };

  return null;
}

/** Why a new password (typed twice) cannot be saved, or null when it can. */
export function newPasswordProblem(password: string, confirmation: string): string | null {
  if (password.length < MIN_PASSWORD_LENGTH) {
    return `Use at least ${MIN_PASSWORD_LENGTH} characters.`;
  }
  if (password !== confirmation) {
    return 'The two passwords do not match.';
  }
  return null;
}

/** How long, after a link is verified in a browser tab, that tab may show the set-password form. */
export const VERIFIED_LINK_WINDOW_MS = 15 * 60 * 1000;

/** What the set-password page stores (per tab) when it verifies a link for `userId`. */
export function verifiedLinkRecord(userId: string, now: number): string {
  return JSON.stringify({ userId, verifiedAt: now });
}

/**
 * Whether a stored verification record lets the signed-in `userId` see the
 * set-password form: same account, verified within the window. Anything else,
 * including an unreadable record, means no.
 */
export function verifiedLinkAllows(record: string | null, userId: string | null, now: number): boolean {
  if (!record || !userId) return false;
  let parsed: unknown;
  try {
    parsed = JSON.parse(record);
  } catch {
    return false; // not a record this page wrote
  }
  if (typeof parsed !== 'object' || parsed === null) return false;
  const { userId: recordedUser, verifiedAt } = parsed as { userId?: unknown; verifiedAt?: unknown };
  if (recordedUser !== userId || typeof verifiedAt !== 'number') return false;
  const age = now - verifiedAt;
  return age >= 0 && age < VERIFIED_LINK_WINDOW_MS;
}

/** Seconds a person waits between password reset emails; Supabase refuses faster repeats. */
export const RESET_EMAIL_COOLDOWN_SECONDS = 60;

/** Whole seconds left until `until` (a timestamp in ms), or 0 when there is no wait. */
export function cooldownSecondsLeft(until: number | null, now: number): number {
  if (until === null) return 0;
  return Math.max(0, Math.ceil((until - now) / 1000));
}

/**
 * Whether Supabase refused a request as too frequent. The throttle only applies
 * to addresses that have an account (one got an email under a minute ago), so
 * the reset forms show it exactly like a success; anything else would reveal
 * which addresses have accounts.
 */
export function isRateLimitError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const { status, code } = error as { status?: unknown; code?: unknown };
  return status === 429 || code === 'over_email_send_rate_limit' || code === 'over_request_rate_limit';
}
